'use client';

import React, { useEffect, useRef, useState } from 'react';
import { type ContentfulComponentProps } from '../type';
import { responsiveBreakpointsPx } from '@/constants';
import { useCssVar, useElementSize } from '@/hooks';

const STICKY_CTA_DISMISSED_KEY = 'stickyCtaDismissed';
const STICKY_BAR_STICK_CLASS = 'stickyBarStick';
const STICKY_BAR_BEHAVIOUR_CLASS = 'stickyBarBehavior';

const INITIAL_OFFSETS = {
    '--sticky-cta-top': 0,
    '--sticky-bar-top': 0,
    '--sticky-bar-reserve': 0,
    '--nav-reserve': 0,
    '--channel-guide-top': 0,
};

const OFFSET_NAMES = Object.keys(INITIAL_OFFSETS) as Array<keyof typeof INITIAL_OFFSETS>;

interface StickyCtaTriggerProps extends ContentfulComponentProps<SiteStickyCta> {
    isTopLevelSticky?: boolean;
}

export function StickyCtaTrigger({ asset, isTopLevelSticky = false }: StickyCtaTriggerProps): React.JSX.Element {
    const scrollUpBehavior = asset.fields.presentation.styles['Scroll-up Behavior'];
    const triggerRef = useRef<HTMLDivElement>(null);
    const updateOffsetsRef = useRef<() => void>(() => {});
    const [offsets, setOffsets] = useState(INITIAL_OFFSETS);
    const [observeNavSize, navSize] = useElementSize();
    const [observeStickyBarSize, stickyBarSize] = useElementSize();

    useCssVar('--sticky-cta-top', offsets['--sticky-cta-top']);
    useCssVar('--sticky-bar-top', offsets['--sticky-bar-top']);
    useCssVar('--sticky-bar-reserve', offsets['--sticky-bar-reserve']);
    useCssVar('--nav-reserve', offsets['--nav-reserve']);
    useCssVar('--channel-guide-top', offsets['--channel-guide-top']);

    useEffect(() => {
        observeNavSize(document.querySelector('[data-componenttype="Global Nav"]'));
        observeStickyBarSize(document.querySelector('[class*="stickybar"]'));

        return () => {
            observeNavSize(null);
            observeStickyBarSize(null);
        };
    }, [observeNavSize, observeStickyBarSize]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const navEntries = performance.getEntriesByType?.('navigation');
        const navEntry = navEntries?.[0] as PerformanceNavigationTiming | undefined;
        if (navEntry?.type === 'reload' || navEntry?.type === 'back_forward') {
            sessionStorage.removeItem(STICKY_CTA_DISMISSED_KEY);
            document.body.classList.remove(STICKY_BAR_STICK_CLASS);
        }
    }, []);

    useEffect(() => {
        let lastScroll = 0;
        const isStickyBarBehavior = scrollUpBehavior !== 'Global Nav';

        document.body.classList.toggle(STICKY_BAR_BEHAVIOUR_CLASS, isStickyBarBehavior);

        if (isTopLevelSticky && isStickyBarBehavior && !isStickyCtaDismissed()) {
            document.body.classList.add(STICKY_BAR_STICK_CLASS);
            updateOffsetsRef.current();
        }
        function scrollHandler() {
            const nav = document.querySelector<HTMLElement>('[data-componenttype="Global Nav"]');
            const isSearchOpen = nav?.dataset.searchOpen === 'true';

            if (isSearchOpen) {
                const top = triggerRef?.current?.getBoundingClientRect()?.top;

                const shouldShowStickyDuringSearch = isTopLevelSticky || (top !== undefined && top <= -1);

                document.body.classList.toggle(STICKY_BAR_STICK_CLASS, shouldShowStickyDuringSearch);

                updateOffsetsRef.current();
                return;
            }

            if (isStickyCtaDismissed()) {
                document.body.classList.remove(STICKY_BAR_STICK_CLASS);
                return;
            }

            const top = triggerRef?.current?.getBoundingClientRect()?.top;
            const dir = lastScroll < window.scrollY ? 'down' : 'up';

            lastScroll = window.scrollY;

            const scrollPosition = window.innerHeight + window.scrollY;

            const bottomPosition = document.body.offsetHeight - 100;

            if (scrollPosition >= bottomPosition) {
                document.body.classList.remove(STICKY_BAR_STICK_CLASS);
                return;
            }

            if (window.scrollY < 10) {
                if (isTopLevelSticky && isStickyBarBehavior && !isStickyCtaDismissed()) {
                    document.body.classList.add(STICKY_BAR_STICK_CLASS);
                    updateOffsetsRef.current();
                } else {
                    document.body.classList.remove(STICKY_BAR_STICK_CLASS);
                }

                return;
            }

            if (scrollUpBehavior === 'Global Nav' && dir === 'up') {
                document.body.classList.remove(STICKY_BAR_STICK_CLASS);
                updateOffsetsRef.current();
            } else if (top !== undefined) {
                if (top <= -1) {
                    document.body.classList.add(STICKY_BAR_STICK_CLASS);
                } else if (!isStickyBarBehavior) {
                    document.body.classList.remove(STICKY_BAR_STICK_CLASS);
                }
            }
        }
        document.addEventListener('scroll', scrollHandler);
        return () => {
            document.removeEventListener('scroll', scrollHandler);
            document.body.classList.remove(STICKY_BAR_BEHAVIOUR_CLASS);
        };
    }, [scrollUpBehavior, isTopLevelSticky]);

    useEffect(() => {
        function updateOffsets() {
            const nav = document.querySelector<HTMLElement>('[data-componenttype="Global Nav"]');
            const stickyBar = document.querySelector<HTMLElement>('[class*="stickybar"]');

            const announcementBannerH =
                parseInt(
                    getComputedStyle(document.documentElement).getPropertyValue('--announcement-banner-height'),
                    10,
                ) || 0;

            const isMobile = window.innerWidth < responsiveBreakpointsPx.md;
            const isSearchOpen = nav?.dataset.searchOpen === 'true';

            const shouldOffsetSticky = window.scrollY < (announcementBannerH || 10) || isSearchOpen;
            const stickyCtaTop = shouldOffsetSticky ? announcementBannerH : 0;

            const top = triggerRef?.current?.getBoundingClientRect()?.top;

            const shouldShowStickyDuringSearch = isTopLevelSticky || (top !== undefined && top <= -1);

            const isSticking =
                document.body.classList.contains(STICKY_BAR_STICK_CLASS) ||
                (isSearchOpen && shouldShowStickyDuringSearch);
            const measuredStickyBarH = stickyBarSize.height || stickyBar?.offsetHeight || 0;
            const stickyH = isSticking ? measuredStickyBarH : 0;
            const navH = navSize.height || nav?.offsetHeight || 0;

            // inFlow bar (position:sticky) is already in document flow — nav sits below it
            // naturally, so no margin-top adjustment needed on the nav
            const isInFlow = stickyBar ? getComputedStyle(stickyBar).position === 'sticky' : false;
            const isGlobalNavBehavior = scrollUpBehavior === 'Global Nav';
            const stickyBarTop = isMobile || isInFlow ? 0 : stickyH;

            // Anchor-jump reservation (MX-1351): scroll-margin-top targets (e.g. offer details) must
            // clear the bar's full height. Unlike --sticky-bar-top — which the nav zeroes for inFlow
            // bars (it sits below them in flow) and only sets while stuck — the offer-details target
            // needs the bar height whenever the bar occupies the top of the viewport, inFlow or fixed.
            // So reserve offsetHeight here with no isInFlow/isSticking guard (mobile uses a bottom bar
            // that never covers a top anchor, hence 0).
            const stickyBarReserve = isMobile ? 0 : measuredStickyBarH;

            let channelGuideTop: number;

            if (isMobile) {
                if (isGlobalNavBehavior && !isSticking) {
                    channelGuideTop = navH;
                } else {
                    channelGuideTop = 0;
                }
            } else {
                if (isGlobalNavBehavior) {
                    if (isSticking) {
                        channelGuideTop = stickyH;
                    } else {
                        channelGuideTop = navH;
                    }
                } else {
                    const isAtPageTop = window.scrollY < 10;
                    channelGuideTop = isAtPageTop ? stickyH + navH : stickyH;
                }
            }

            const nextOffsets = {
                '--sticky-cta-top': stickyCtaTop,
                '--sticky-bar-top': stickyBarTop,
                '--sticky-bar-reserve': stickyBarReserve,
                '--nav-reserve': navH,
                '--channel-guide-top': channelGuideTop,
            };

            setOffsets((current) => {
                for (const name of OFFSET_NAMES) {
                    if (current[name] !== nextOffsets[name]) {
                        return nextOffsets;
                    }
                }

                return current;
            });
        }

        updateOffsetsRef.current = updateOffsets;
        updateOffsets();

        const mo = new MutationObserver(() => {
            requestAnimationFrame(updateOffsets);
        });

        mo.observe(document.body, {
            attributes: true,
            attributeFilter: ['class'],
        });

        mo.observe(document.documentElement, {
            attributes: true,
            subtree: true,
            childList: true,
        });

        window.addEventListener('scroll', updateOffsets, { passive: true });
        window.addEventListener('resize', updateOffsets);

        return () => {
            mo.disconnect();
            window.removeEventListener('scroll', updateOffsets);
            window.removeEventListener('resize', updateOffsets);
        };
    }, [scrollUpBehavior, isTopLevelSticky, navSize.height, stickyBarSize.height]);

    return <div ref={triggerRef} data-note="sticky-trigger"></div>;
}

function isStickyCtaDismissed() {
    return Boolean(sessionStorage.getItem(STICKY_CTA_DISMISSED_KEY));
}
